import { validateCpr, validateEmail, validatePhone } from '@flos/react-ui';
import {
    type YoungAccidentDTO,
    type AdultAccidentDTO,
    type ChildAccidentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/accident';
import { type PrivateContentDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/content';
import {
    type CatDTO,
    type DogDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/pet';
import { type AllCoverablesDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { type SingleTripCancellationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/stc';
import { type SingleTripTravelDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/stt';
import { filterRemovedCoverables, reformatCprWithoutHyphen } from '../../../utils/missingInfoUtil';
import { replaceAllCharactersButNumbers } from '../../../utils/stringUtil';

const isMissingPetChipId = (data: CatDTO | DogDTO): boolean => {
    return data.hasChipNumber ? true : !data.chipIdNumber;
};

const isMissingPetPoliceDogId = (data: DogDTO): boolean => {
    if (data.dogUse && data.dogUse === 'Tjenestehund' && !data.policeDogID) return true;
    return false;
};

const isMissingDogData = (data: DogDTO): boolean => {
    return isMissingPetChipId(data) || isMissingPetPoliceDogId(data);
};

const isMissingSingleTripData = (
    data: SingleTripTravelDTO | SingleTripCancellationDTO
): boolean => {
    return (
        !!data?.numberOfPersonOutsideHousehold &&
        !!data.insuredPersonOutsideHouse &&
        data.numberOfPersonOutsideHousehold > (data.insuredPersonOutsideHouse?.length ?? 0)
    );
};

const isMissingChildAccidentData = (data: ChildAccidentDTO): boolean => {
    return (
        data.isDummyContact ??
        !data.cprNumber ??
        !validateCpr(reformatCprWithoutHyphen(data.cprNumber)) ??
        !data.address
    );
};

const isMissingAdultYoungAccidentOtherPersonData = (
    data: AdultAccidentDTO | YoungAccidentDTO
): boolean => {
    if (data.isDummyContact) return true;

    if (data.willSpinOff) {
        // if accident willSpinOff, validate if there is any missing information
        if (!validateCpr(replaceAllCharactersButNumbers(data.cprNumber ?? ''))) return true;
        if (!data.dawaId) return true;
        if (!validatePhone(data.phoneNumber) && !validateEmail(data.email)) return true;
    }

    return false;
};

const isMissingPRCCoinsuredPartner = (data: PrivateContentDTO): boolean => {
    return !!(data?.hasCoinsured && !data.coinsuredName);
};

const isMissingPRCCohousing = (data: PrivateContentDTO): boolean => {
    return !!(
        data?.hasCohousing && data.numberInHousehold !== data.insuredPersonCohousings?.length
    );
};

const isMissingPrivateContentData = (data: PrivateContentDTO): boolean => {
    return isMissingPRCCoinsuredPartner(data) || isMissingPRCCohousing(data);
};

const doesAnyCoverableHaveMissingInformation = (coverables: AllCoverablesDTO): boolean => {
    const filteredList = filterRemovedCoverables(coverables);

    for (const key of Object.keys(filteredList)) {
        switch (key) {
            case 'cats':
                if (filteredList.cats?.some((item) => isMissingPetChipId(item))) return true;
                break;
            case 'dogs':
                if (filteredList.dogs?.some((item) => isMissingDogData(item))) return true;
                break;
            case 'singleTripTravel':
                if (filteredList.singleTripTravel?.some((item) => isMissingSingleTripData(item)))
                    return true;
                break;
            case 'singleTripCancellation':
                if (
                    filteredList.singleTripCancellation?.some((item) =>
                        isMissingSingleTripData(item)
                    )
                )
                    return true;
                break;
            case 'childAccidents':
                if (filteredList.childAccidents?.some((item) => isMissingChildAccidentData(item)))
                    return true;
                break;
            case 'privateContent':
                if (filteredList.privateContent?.some((item) => isMissingPrivateContentData(item)))
                    return true;
                break;
            case 'adultAccidents':
                if (
                    filteredList.adultAccidents?.some((item) =>
                        isMissingAdultYoungAccidentOtherPersonData(item)
                    )
                )
                    return true;
                break;
            case 'youngAccidents':
                if (
                    filteredList.youngAccidents?.some((item) =>
                        isMissingAdultYoungAccidentOtherPersonData(item)
                    )
                )
                    return true;
                break;
            case 'cars':
                if (
                    filteredList.cars?.some(
                        (item) =>
                            !item.odometerReading &&
                            !item.hideMileageRelatedAttribute &&
                            item.hasOdometer
                    )
                )
                    return true;
                break;
            case 'houses':
                if (
                    filteredList.houses?.some(
                        (item) =>
                            item.buyingHouse === false && item.outputBuildingReportPending !== false
                    )
                )
                    return true;
                break;
            case 'holidayHomes':
                if (
                    filteredList.holidayHomes?.some(
                        (item) =>
                            item.buyingHouse === false && item.outputBuildingReportPending !== false
                    )
                )
                    return true;
                break;
            case 'caravans':
                if (filteredList.caravans?.some((item) => item.stationary && !item.regNumber))
                    return true;
                break;
        }
    }
    return false;
};

export {
    isMissingPetChipId,
    isMissingPetPoliceDogId,
    isMissingDogData,
    isMissingSingleTripData,
    doesAnyCoverableHaveMissingInformation,
    isMissingChildAccidentData,
    isMissingPrivateContentData,
    isMissingPRCCoinsuredPartner,
    isMissingPRCCohousing,
    isMissingAdultYoungAccidentOtherPersonData
};
